<script setup lang="ts">
  // Vue core imports
  import type { PropType } from 'vue'
  import { computed, onMounted, ref, watch } from 'vue'

  import { useGlobal } from '@common/core-layer/composables/useGlobal'

  import { useI18n } from 'vue-i18n'

  // Internal imports
  import CPButton from '@ui/components/CPButton.vue'

  import CPForm from '@ui/components/CPForm.vue'

  import CPFormItem from '@ui/components/CPFormItem.vue'

  import CPInput from '@ui/components/CPInput.vue'

  import CPText from '@ui/components/CPText.vue'

  import SignInWithGoogle from './SignInWithGoogle.vue'

  import SignInWithGoogleKollab from './SignInWithGoogleKollab.vue'

  import { createElementId } from '../../../core-layer/utils'

  import { useAuth } from '../../composables/useAuth'

  import { useCaptchaChallenge } from '../../composables/useCaptchaChallenge'

  import { Screens } from '../../types/auth'

  import { getErrorMessage } from '../../utils'

  import Recaptcha from '../captcha/ReCaptcha.vue'

  // Props definitions
  const props = defineProps({
    referrerUser: {
      type: Object as PropType<{
        email: string
        name: string
        _id?: string
      }>,
      default: null,
      required: false,
    },
    email: {
      type: String,
      default: '',
    },
    isSsoDisabled: {
      type: Boolean,
      default: false,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    (e: 'changeScreen', value: any): void
    (e: 'loginCallback', value: any): void
  }>()

  const { t } = useI18n()
  const { isGokollab } = useGlobal()

  // Refs and reactive variables
  const errorMessage = ref()

  const loading = ref(false)

  const loginDisabled = ref(false)

  const { showRecaptcha, reCaptchaToken, onVerify, onError, onExpired, handleRateLimit } = useCaptchaChallenge(loginDisabled)

  const emailError = ref('')

  const rules = {
    email: [
      {
        required: true,
        message: 'Email is required',
        trigger: ['input', 'blur'],
      },
      {
        type: 'email',
        message: 'Please enter a valid email',
        trigger: ['input', 'blur'],
      },
    ],
  }

  const formValue = ref({
    email: props.email || '',
  })

  // Computed properties
  const isFormValid = computed(() => {
    return formValue.value.email && !emailError.value
  })

  // Refs
  const formRef = ref()

  const handleValidateClick = async () => {
    loading.value = true
    const result = await validateForm()
    if (!result) {
      loading.value = false
      return
    }

    try {
      const auth = useAuth()
      await auth.migrateEmail(formValue.value.email.trim())

      const response = await auth.generateOTPForLogin(formValue.value.email.trim(), 'login', reCaptchaToken.value)

      const otpToken = response?.data?.otpToken
      const isNewUser = response?.data?.isNewUser
      if (otpToken) {
        emit('changeScreen', {
          email: formValue.value.email,
          otpToken,
          isNewUser,
          page: Screens.VERIFY_SECURE_CODE,
          action: 'login',
        })
      } else {
        errorMessage.value = t('auth.messages.failedToGenerateSecureCode')
      }
      showRecaptcha.value = false
    } catch (error: any) {
      if (!handleRateLimit(error)) {
        errorMessage.value = getErrorMessage(error)
      }
    } finally {
      loading.value = false
    }
  }

  const onFormValueChange = () => {
    errorMessage.value = null
  }

  // Lifecycle hooks
  onMounted(() => {
    if (props.email) {
      formValue.value.email = props.email
    }
  })

  // Watch effects
  watch(
    () => props.email,
    (newEmail: any) => {
      if (newEmail) {
        formValue.value.email = newEmail
      }
    }
  )

  watch(
    () => formValue.value.email,
    () => {
      emailError.value = ''
      errorMessage.value = null
    }
  )

  // User-defined functions
  async function validateForm() {
    try {
      await formRef.value.getForm().validate()
      return true
    } catch (e) {
      console.error('error', e)
      return false
    }
  }
</script>

<template>
  <div>
    <SignInWithGoogleKollab
      v-if="!props.isSsoDisabled && isGokollab"
      login-type="SIGN_IN"
      :referrer-user="props.referrerUser"
      @changeScreen="(val: any) => emit('changeScreen', val)"
      @loginCallback="(val: any) => emit('loginCallback', val)"
    />
    <SignInWithGoogle
      v-else-if="!props.isSsoDisabled"
      login-type="SIGN_IN"
      :referrer-user="props.referrerUser"
      @changeScreen="(val: any) => emit('changeScreen', val)"
      @loginCallback="(val: any) => emit('loginCallback', val)"
    />

    <CPForm
    :id="createElementId('login-secure', 'modal', 'login-secure-form')"
    ref="formRef"
    class="flex flex-col items-center"
    :model="formValue"
    :rules="rules"
    @keyup.enter="handleValidateClick"
  >
    <CPFormItem class="w-full" :label="t('auth.email')" required path="email" :show-require-mark="true" label-size="md">
      <CPInput
        :id="createElementId('login-secure', 'modal', 'email-input')"
        v-model="formValue.email"
        size="lg"
        :placeholder="t('auth.emailPlaceholder')"
        @input="onFormValueChange()"
      />
      <template #feedback>
        <CPText size="sm" :class="emailError ? 'text-danger' : 'text-clientportal-font-secondary'">
          {{ emailError || t('auth.secureCodeSentToEmail') }}
        </CPText>
      </template>
    </CPFormItem>

    <div v-if="errorMessage" :id="createElementId('login-secure', 'modal', 'error-message')" class="text-danger w-full">
      <CPText size="sm">{{ errorMessage }}</CPText>
    </div>

    <Recaptcha
      v-if="showRecaptcha"
      :id="createElementId('login-secure', 'modal', 'recaptcha')"
      class="flex w-full justify-center"
      @expired="onExpired"
      @verify="onVerify"
      @error="onError"
    />

    <CPButton
      :id="createElementId('login-secure', 'modal', 'generate-code-button')"
      class="w-full shadow-sm mt-1"
      type="primary"
      size="lg"
      :loading="loading"
      :disabled="loading || loginDisabled || !isFormValid"
      @click="handleValidateClick"
    >
      {{ t('auth.generateSecureCode') }}
    </CPButton>

    <CPButton
      :id="createElementId('login-secure', 'modal', 'login-with-password-button')"
      class="w-full mt-2"
      type="secondary"
      size="lg"
      @click="emit('changeScreen', { page: Screens.LOGIN })"
    >
      {{ t('auth.loginWithPassword') }}
    </CPButton>

    <div class="text-secondary flex w-full justify-center items-center gap-1 mt-4">
      <CPText size="md" weight="regular" html-tag="span">
        {{ t('auth.newUser') }}
      </CPText>
      <CPButton
        :id="createElementId('login-secure', 'modal', 'open-signup-button')"
        type="text"
        color="secondary"
        size="xs"
        class="cursor-pointer hover:underline"
        @click="emit('changeScreen', { page: Screens.SIGN_UP })"
      >
        <CPText size="md" weight="semibold" html-tag="span">
          {{ t('auth.signUp') }}
        </CPText>
      </CPButton>
    </div>
  </CPForm>
  </div>
</template>

<style scoped>
  .hr-divider:not(.hr-divider--vertical) {
    margin: 0px;
  }
  .hr-form {
    gap: 16px;
  }
</style>
